"use client";

import React from "react";
import { MoonStar, BatteryWarning, Brain, Activity, ShieldAlert, HeartPulse } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const symptoms = [{
  id: 1,
  title: "Chronic Snoring",
  description: "Frequent, loud snoring is often the first sign of an airway obstruction that prevents your body from reaching deep, restorative sleep.",
  icon: MoonStar
}, {
  id: 2,
  title: "Daytime Fatigue",
  description: "Feeling exhausted even after a full night's rest is a key indicator that your breathing is being interrupted throughout the night.",
  icon: BatteryWarning
}, {
  id: 3,
  title: "Morning Headaches",
  description: "Waking up with head pain or a persistent dry mouth often stems from poor nighttime oxygen levels and forced mouth breathing.",
  icon: Brain
}, {
  id: 4,
  title: "Gasping for Air",
  description: "Choking or gasping sounds during sleep are serious warning signs of Obstructive Sleep Apnea that require professional evaluation.",
  icon: Activity
}, {
  id: 5,
  title: "Irritability & Fog",
  description: "Difficulty concentrating, memory lapses, and mood swings are common side effects of fragmented, low-quality sleep cycles.",
  icon: ShieldAlert
}, {
  id: 6,
  title: "Cardiovascular Strain",
  description: "Untreated sleep apnea is closely linked to high blood pressure and heart strain as your body struggles to breathe while you rest.",
  icon: HeartPulse
}];
export default function TheSleepGap() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="99171" id="common-symptoms" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -left-[10%] h-[70%] w-[50%] animate-pulse rounded-full bg-primary/5 blur-[120px] duration-[8000ms]" />
        <div className="absolute top-[40%] -right-[20%] h-[80%] w-[60%] animate-pulse rounded-full bg-secondary/5 blur-[150px] duration-[12000ms]" />
        <div className="absolute -bottom-[30%] left-[20%] h-[60%] w-[50%] animate-pulse rounded-full bg-primary/10 blur-[100px] duration-[10000ms]" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        {}
        <div ref={headerReveal.ref} className={`mx-auto max-w-3xl text-center transition-all duration-1000 ${headerReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          <h2 className="font-sans text-4xl font-bold tracking-tight text-primary sm:text-5xl md:text-6xl">
            Common Symptoms
          </h2>
          <p className="text-muted-foreground mt-6 text-lg leading-relaxed md:text-xl">
            Many adults live with sleep-disordered breathing without realizing it. Recognizing these warning signs is the first step toward reclaiming your health and vitality.
          </p>
        </div>

        {}
        <div ref={gridReveal.ref} className={`mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 transition-all duration-1000 delay-200 ${gridReveal.isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>
          {symptoms.map((symptom, i) => {
          const Icon = symptom.icon;
          return <div data-index={i} key={symptom.id} className="group h-full">
                <Card className="bg-card border-border hover:border-secondary/60 hover:shadow-[0_0_30px_-5px_var(--color-secondary)] relative h-full overflow-hidden backdrop-blur-md transition-all duration-500">
                  {}
                  <div className="absolute inset-0 bg-gradient-to-br from-secondary/0 via-secondary/0 to-secondary/5 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                  
                  <CardHeader className="pb-4">
                    <div className="bg-secondary/10 text-secondary mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-500 group-hover:scale-110 group-hover:bg-secondary/20">
                      <Icon className="h-6 w-6" />
                    </div>
                    <CardTitle className="text-xl tracking-tight text-primary">
                      {symptom.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-base leading-relaxed">
                      {symptom.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>

        {}
        <div className="mt-16 flex justify-center">
          <Button size="lg" className="rounded-full bg-secondary text-secondary-foreground px-8 py-6 text-lg shadow-[0_0_20px_-5px_var(--color-secondary)] transition-all hover:bg-secondary/90 hover:shadow-[0_0_30px_-5px_var(--color-secondary)]">
            Start Your Assessment
          </Button>
        </div>
      </div>
    </section>;
}